import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Form, Spin, InputNumber, message } from 'antd';
import { Banner } from 'components/ui/Banner';
import PageMeta from 'components/PageMeta/PageMeta';
import Card from 'components/Card/Card';
import RadioCards from 'components/RadioCards/RadioCards';
import { getPreviousStep, getNextStep, stepConfigAtom } from '../../atoms';
import { FAMILY_APPLICATION_STEPS, FAMILY_SPOUSE_EDUCATION_LEVELS } from '../../familyApplicationConstants';
import { useAtomValue } from 'jotai';
import familyService from 'services/family.service';
import { useAsyncCallback } from 'react-async-hook';
import { DatePicker } from 'components/ui/DatePicker';
import { uniq } from 'lodash-es';
import { getOtherAdultFirstName } from '../../utils';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';

export function FamilyAppSpouseEducation({
  application,
  setApplication,
  household,
  loading,
  setStep,
  readOnly = false,
}) {
  const stepConfig = useAtomValue(stepConfigAtom);

  // Form state
  const [form] = Form.useForm();
  const inSchoolFormValue = Form.useWatch('inSchool', form);
  const educationDetailsFormValue = Form.useWatch('educationDetails', form);
  const [submitting, setSubmitting] = useState(false);

  const isCompletedBySponsor = application?.completedBySponsor === true;
  const spouse = application?.additionalParents?.[0];

  // Track previously stored education IDs
  const prevEducationIdsRef = useRef([]);

  // Fetch education data
  const { loading: loadingEducation, execute: fetchEducation } = useAsyncCallback(async () => {
    if (!spouse?.id) return [];

    try {
      const data = await familyService.getParentEducations(spouse.id);

      // Set the form values
      form.setFieldsValue({
        educationDetails: data,
        educationLevels: uniq(data.map((d) => d.type)),
      });
      // Store the IDs for comparison when submitting updates
      prevEducationIdsRef.current = data.map((d) => d.id).filter(Boolean);
      return data;
    } catch (error) {
      console.error('Error fetching education data:', error);
      return [];
    }
  });

  // Fetch education data when spouse ID changes
  useEffect(() => {
    if (spouse?.id) {
      fetchEducation();
    }
  }, [fetchEducation, spouse?.id]);

  // Initialize form with existing data
  useEffect(() => {
    form.setFieldsValue({
      inSchool: spouse?.inSchool,
    });
  }, [spouse, form]);

  const onSubmit = useCallback(
    async (values) => {
      const _goToNextStep = (_newApplication) => {
        // Use getNextStep to determine the next step
        const nextStep = getNextStep(FAMILY_APPLICATION_STEPS.SPOUSE_EDUCATION, _newApplication, stepConfig);
        setStep({
          applicationId: _newApplication?.id,
          newStep: nextStep,
        });
      };

      if (readOnly) {
        _goToNextStep(application);
        return;
      }

      setSubmitting(true);
      try {
        // Update the spouse info with inSchool value
        const updatedSpouse = await familyService.addUpdateAdult(
          household?.id,
          application?.additionalParents?.[0]?.id ?? null,
          {
            ...application?.additionalParents?.[0],
            inSchool: values.inSchool,
          },
        );

        // Get previous and current education IDs
        const prevIds = prevEducationIdsRef.current;
        const currentDetails = values.educationDetails || [];
        const currentIds = currentDetails.map((d) => d.id).filter(Boolean);

        // If inSchool is false, delete all previous educations
        if (!values.inSchool && prevIds.length > 0) {
          for (const id of prevIds) {
            await familyService.deleteParentEducation(updatedSpouse.id, id);
          }
        } else if (values.inSchool) {
          // Save/update each education item for the spouse
          for (const edu of currentDetails) {
            await familyService.addUpdateParentEducations(updatedSpouse.id, edu);
          }

          // Delete educations that are no longer present
          for (const id of prevIds) {
            if (!currentIds.includes(id)) {
              await familyService.deleteParentEducation(updatedSpouse.id, id);
            }
          }
        }

        // Update application state
        const updatedApplication = {
          ...application,
          additionalParents: [updatedSpouse],
        };

        const savedApplication = await familyService.saveApplication(updatedApplication);
        setApplication(savedApplication);

        // Move to next step
        _goToNextStep(savedApplication);
      } catch (error) {
        message.error('Something went wrong');
        newrelic?.noticeError?.(error);
      } finally {
        setSubmitting(false);
      }
    },
    [application, household?.id, readOnly, setApplication, setStep, stepConfig],
  );

  // Description: When the user changes the level of study, we need to update the form values
  // to reflect the new levels of study.
  // We also need to add new levels of study to the form values.
  // We also need to delete the old levels of study from the form values.
  const handleLevelChange = (selectedLevels) => {
    const currentDetails = educationDetailsFormValue || [];

    // Add new levels
    const newDetails = selectedLevels.map((level) => {
      return currentDetails.find((d) => d.type === level) || { type: level };
    });

    form.setFieldsValue({ educationLevels: selectedLevels, educationDetails: newDetails });
  };

  if (loading || loadingEducation) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <Spin spinning={submitting}>
      <PageMeta title="Spouse Education Information" />

      <Form form={form} layout="vertical" onFinish={onSubmit} disabled={readOnly}>
        <div className="p-7 pt-0">
          <Banner
            question={
              isCompletedBySponsor
                ? `Is ${getOtherAdultFirstName(application?.additionalParents?.[0])} currently a student?`
                : 'Are you currently a student?'
            }
            className="my-12"
          />

          <div className="mb-12">
            <Form.Item
              name="inSchool"
              rules={[
                {
                  required: true,
                  message: `Please specify if ${isCompletedBySponsor ? 'your spouse' : 'you'} is currently a student`,
                },
              ]}
              className="mb-4"
            >
              <RadioCards
                options={[
                  {
                    value: true,
                    label: `Yes`,
                  },
                  {
                    value: false,
                    label: `No`,
                  },
                ]}
              />
            </Form.Item>
          </div>

          {inSchoolFormValue && (
            <>
              {/* Level of Study Selection */}
              <Form.Item
                name="educationLevels"
                label="What level?"
                rules={[{ required: true, message: 'Please select at least one level of study' }]}
              >
                <RadioCards
                  multiple
                  onChange={handleLevelChange}
                  options={[
                    {
                      value: FAMILY_SPOUSE_EDUCATION_LEVELS.UNDERGRADUATE,
                      label: 'Undergraduate degree',
                    },
                    {
                      value: FAMILY_SPOUSE_EDUCATION_LEVELS.GRADUATE,
                      label: 'Graduate degree',
                    },
                    {
                      value: FAMILY_SPOUSE_EDUCATION_LEVELS.OTHER_POST_SECONDARY,
                      label: 'Trade/technical program',
                    },
                  ]}
                />
              </Form.Item>

              {/* Repeat for each selected level */}
              <Form.List name="educationDetails">
                {(fields) => (
                  <>
                    {fields.map((field, idx) => {
                      const level = educationDetailsFormValue?.[field.name]?.type;

                      return (
                        <Card
                          key={field.key}
                          title={
                            level === FAMILY_SPOUSE_EDUCATION_LEVELS.UNDERGRADUATE
                              ? 'Undergraduate degree'
                              : level === FAMILY_SPOUSE_EDUCATION_LEVELS.GRADUATE
                              ? 'Graduate degree'
                              : 'Trade/technical program'
                          }
                          className="mb-8 border shadow-sm"
                        >
                          <div className="grid grid-cols-2 gap-4">
                            <Form.Item
                              name={[field.name, 'startDate']}
                              label={<span className="font-semibold">Semester/Term Start Date</span>}
                              rules={[{ required: true, message: 'Please select start date' }]}
                            >
                              <DatePicker data-testid={`education-start-date-${idx}`} />
                            </Form.Item>

                            <Form.Item
                              name={[field.name, 'endDate']}
                              label={<span className="font-semibold">Expected End Date</span>}
                              rules={[{ required: true, message: 'Please select end date' }]}
                            >
                              <DatePicker data-testid={`education-end-date-${idx}`} />
                            </Form.Item>
                          </div>

                          <Form.Item
                            name={[field.name, 'creditHours']}
                            label={
                              level === FAMILY_SPOUSE_EDUCATION_LEVELS.OTHER_POST_SECONDARY
                                ? 'How many credit/clock hours?'
                                : 'How many credit hours?'
                            }
                            rules={[
                              { required: true, message: 'Please enter credit hours' },
                              ...(level === FAMILY_SPOUSE_EDUCATION_LEVELS.GRADUATE
                                ? [
                                    {
                                      validator: (_, value) => {
                                        if (value > 18) {
                                          return Promise.reject(
                                            new Error('Please verify credit hours (typical grad range: 6-12)'),
                                          );
                                        } else if (value > 12) {
                                          return Promise.reject(
                                            new Error(
                                              "That's more than 12 credit hours. Most full-time grad students take 9-12. Please double-check.",
                                            ),
                                          );
                                        }

                                        return Promise.resolve();
                                      },
                                    },
                                  ]
                                : []),
                              ...(level === FAMILY_SPOUSE_EDUCATION_LEVELS.UNDERGRADUATE
                                ? [
                                    {
                                      validator: (_, value) => {
                                        if (value > 30) {
                                          return Promise.reject(
                                            new Error('Please verify credit hours (typical range: 12-18)'),
                                          );
                                        } else if (value > 21) {
                                          return Promise.reject(
                                            new Error(
                                              "That's more than 21 credit hours. Most full-time students take 12-18. Please double-check.",
                                            ),
                                          );
                                        }

                                        return Promise.resolve();
                                      },
                                    },
                                  ]
                                : []),
                            ]}
                            className="mb-0"
                          >
                            <InputNumber
                              data-testid={`education-credit-hours-${idx}`}
                              size="large"
                              min={1}
                              max={level === FAMILY_SPOUSE_EDUCATION_LEVELS.OTHER_POST_SECONDARY ? 10000 : 200}
                              addonAfter="hour(s)"
                              controls={false}
                              className="w-44"
                            />
                          </Form.Item>
                        </Card>
                      );
                    })}
                  </>
                )}
              </Form.List>
            </>
          )}
        </div>

        <ApplicationWizardActions
          applicationIsReadOnly={readOnly}
          backButtonProps={{
            disabled: submitting,
            onClick: () => {
              // Use getPreviousStep to determine the previous step
              const previousStep = getPreviousStep(FAMILY_APPLICATION_STEPS.SPOUSE_EDUCATION, application, stepConfig);

              setStep({
                applicationId: application?.id,
                newStep: previousStep,
              });
            },
          }}
          nextButtonProps={{
            loading: submitting,
            htmlType: 'submit',
          }}
        />
      </Form>
    </Spin>
  );
}

export default FamilyAppSpouseEducation;
